import { size, keys, last, trimStart } from "lodash";
import { Platform } from "react-native";
import * as actions from "./actions";

import { QuestionKind, ScreenType, META_OPTION_ACTION } from "consts";
import { navForward } from "state/modules/navigation";
import { submitFeedback } from "state/modules/feedback";
import { setName, setTitle } from "state/modules/customer";
import { setLinkedOptionForItem } from "state/modules/survey";
import { getSurveyQuestionnaire } from "state/modules/survey/selectors";

const createTelemetryData = question => {
  const ts = new Date();
  const duration = Math.max(0, ts.getTime() - question.startTime);
  return { ts, duration, back_counter: 0 };
};

// OPTIONS
// ==========================

export const _addOption = actions.addOption;
export const _clearQuestion = actions.clearQuestion;
export const _setOptionOtherText = actions.setOptionOtherText;

const optionActionMap = {
  add: actions.addOption,
  remove: actions.removeOption,
  set: actions.setOption,
};

// TODO: Combine updateOption and updateSubOption
const updateOption = (action, question, option) => dispatch => {
  const telemetry = createTelemetryData(question);
  dispatch(
    optionActionMap[action](
      question.virtual_id,
      option.virtual_id,
      option.id,
      telemetry,
    ),
  );
  dispatch(validateCurrentSection(question));
};

export const selectOption = (question, option) => (dispatch, getState) => {
  dispatch(resetLastResponseTimer());
  const state = getState();
  const questionnaire = getSurveyQuestionnaire(state);
  const maxSelectionCount = question.max_option_selection_count;
  const currentSection = last(state.navigation.sectionStack);

  const questionResponses = state.response.questions;
  const questionResponse = questionResponses[question.virtual_id] || {};
  const optionResponses = questionResponse.options || {};
  const isSelected = optionResponses[option.virtual_id];

  const shouldShowItemList =
    option.display_products && (!isSelected || maxSelectionCount === 1);

  if (shouldShowItemList) {
    dispatch(setLinkedOptionForItem(question.virtual_id, option.virtual_id));
  }

  if (question.kind === QuestionKind.GENDER) {
    dispatch(setTitle(option));
  }

  dispatch(checkOptionMetaActions(question, option));

  if (maxSelectionCount === 1) {
    if (!isSelected) {
      dispatch(updateOption("set", question, option));
      dispatch(checkDecider(question, option));
    }

    if (
      !option.is_other &&
      currentSection.questions.length === 1 &&
      !option.display_products &&
      !state.survey.isQuiz &&
      !questionnaire.config?.disable_auto_nav
    ) {
      // let question view cache updated selected options of prev question before updating question
      setTimeout(() => dispatch(navForward()), 50);
    }
    return;
  }

  if (isSelected) {
    return dispatch(updateOption("remove", question, option));
  }

  if (maxSelectionCount > 1 && size(optionResponses) >= maxSelectionCount) {
    const message = `Cannot select more than ${maxSelectionCount} options`;
    dispatch(actions.showModal(message));
    return;
  }

  dispatch(updateOption("add", question, option));
};

export const setOption = (question, option) => dispatch => {
  dispatch(resetLastResponseTimer());
  dispatch(updateOption("set", question, option));
};

// SUBOPTIONS
// ==========================

export const _addSubOption = actions.addSubOption;
export const _setSubOptionOtherText = actions.setSubOptionOtherText;

const subOptionActionMap = {
  add: actions.addSubOption,
  remove: actions.removeSubOption,
  set: actions.setSubOption,
};

const updateSubOption = (action, question, option, subOption) => dispatch => {
  const telemetry = createTelemetryData(question);
  dispatch(
    subOptionActionMap[action](
      question.virtual_id,
      option.virtual_id,
      subOption.virtual_id,
      subOption.id,
      telemetry,
    ),
  );
  dispatch(validateCurrentSection(question));
};

export const setSubOption = (question, option, subOption) => dispatch => {
  dispatch(resetLastResponseTimer());
  dispatch(updateSubOption("set", question, option, subOption));
};

export const selectSubOption = (question, option, subOption) => (
  dispatch,
  getState,
) => {
  dispatch(resetLastResponseTimer());
  const { questions: questionResponses } = getState().response;
  const maxSelectionCount = question.max_sub_option_selection_count;
  const selectedOptionCount = question.max_option_selection_count;

  const questionResponse = questionResponses[question.virtual_id] || {};
  const optionResponses = questionResponse.options || {};
  const optionResponse = optionResponses[option.virtual_id] || {};
  const subOptionResponses = optionResponse.subOptions || {};
  const isSelected = subOptionResponses[subOption.virtual_id];

  const shouldShowItemList =
    subOption.display_products && (!isSelected || maxSelectionCount === 1);

  if (shouldShowItemList) {
    dispatch(
      setLinkedOptionForItem(
        question.virtual_id,
        option.virtual_id,
        subOption.virtual_id,
      ),
    );
  }

  if (maxSelectionCount === 1 && selectedOptionCount === 1) {
    if (!isSelected) {
      dispatch(updateSubOption("set", question, option, subOption));
    }
    if (!subOption.is_other && !subOption.display_products) {
      // let question view cache updated selected options of prev question before updating question
      setTimeout(() => dispatch(navForward()), 50);
    }
    return;
  }

  if (maxSelectionCount > 1 && size(subOptionResponses) >= maxSelectionCount) {
    const message = `Cannot select more than ${maxSelectionCount} options`;
    dispatch(actions.showModal(message));
    return;
  }

  const action = isSelected ? "remove" : "add";
  dispatch(updateSubOption(action, question, option, subOption));
};

// OTHER TEXT
// ==========================

export const setOptionOtherText = (
  question,
  option,
  other_text,
) => dispatch => {
  dispatch(resetLastResponseTimer());

  if (question.kind === QuestionKind.NAME) {
    dispatch(setName(other_text));
  }

  const telemetry = createTelemetryData(question);
  dispatch(
    actions.setOptionOtherText(
      question.virtual_id,
      option.virtual_id,
      option.id,
      trimStart(other_text),
      telemetry,
    ),
  );
  dispatch(validateCurrentSection(question));
};

export const setSubOptionOtherText = (
  question,
  option,
  subOption,
  other_text,
) => dispatch => {
  dispatch(resetLastResponseTimer());
  const telemetry = createTelemetryData(question);
  dispatch(
    actions.setSubOptionOtherText(
      question.virtual_id,
      option.virtual_id,
      subOption.virtual_id,
      subOption.id,
      trimStart(other_text),
      telemetry,
    ),
  );
  dispatch(validateCurrentSection(question));
};

// FILE
// ==========================

export const addOptionFile = (question, option, uri, type) => dispatch => {
  const telemetry = createTelemetryData(question);
  const id = new Date().getTime();

  const path = Platform.OS === "android" ? "file://" + uri : uri;
  dispatch(
    actions.addOptionFile(
      question.virtual_id,
      option.virtual_id,
      option.id,
      id,
      path,
      type,
      telemetry,
    ),
  );
};

export const removeOptionFile = (question, option, id) => dispatch => {
  const telemetry = createTelemetryData(question);
  dispatch(
    actions.removeOptionFile(
      question.virtual_id,
      option.virtual_id,
      id,
      telemetry,
    ),
  );
};

export const addSubOptionFile = (
  question,
  option,
  subOption,
  uri,
  type,
) => dispatch => {
  const telemetry = createTelemetryData(question);
  const id = new Date().getTime();
  const path = Platform.OS === "android" ? "file://" + uri : uri;
  dispatch(
    actions.addSubOptionFile(
      question.virtual_id,
      option.virtual_id,
      subOption.virtual_id,
      subOption.id,
      id,
      path,
      type,
      telemetry,
    ),
  );
};

export const removeSubOptionFile = (
  question,
  option,
  subOption,
  id,
) => dispatch => {
  const telemetry = createTelemetryData(question);
  dispatch(
    actions.removeSubOptionFile(
      question.virtual_id,
      option.virtual_id,
      subOption.virtual_id,
      id,
      telemetry,
    ),
  );
};

export const checkOptionMetaActions = (question, option) => dispatch => {
  if (!question.allow_option_meta_actions) {
    return;
  }

  if (option.meta_action === META_OPTION_ACTION.SELECT_ALL) {
    dispatch(selectAllOptions(question));
  } else if (option.meta_action === META_OPTION_ACTION.SELECT_NONE) {
    dispatch(DeselectAllOptions(question));
  } else {
    dispatch(updateNonMetaOptions(question, option));
  }
};

const selectAllOptions = question => dispatch => {
  question.options
    .filter(option => !option.meta_action)
    .forEach(option => {
      dispatch(updateOption("add", question, option));
    });
  const noneOption = question.options.find(
    option => option.meta_action === META_OPTION_ACTION.SELECT_NONE,
  );
  if (noneOption) {
    dispatch(updateOption("remove", question, noneOption));
  }
};

const DeselectAllOptions = question => dispatch => {
  question.options.forEach(option => {
    dispatch(updateOption("remove", question, option));
  });
};

const updateNonMetaOptions = (question, option) => (dispatch, getState) => {
  const { response } = getState();
  const isSelected =
    response.questions[question.virtual_id]?.options?.[option.virtual_id];
  const metaActionType = isSelected
    ? META_OPTION_ACTION.SELECT_ALL
    : META_OPTION_ACTION.SELECT_NONE;
  const metaOption = question.options.find(
    o => o.meta_action === metaActionType,
  );
  if (metaOption) {
    dispatch(updateOption("remove", question, metaOption));
  }
};

// MISC
// ==========================

export const hideModal = actions.hideModal;

const checkDecider = (question, option) => dispatch => {
  // score => 0: positive, 1: neutral, 2: negative
  const validDecider =
    question.is_decider &&
    typeof option.score === "number" &&
    option.score >= 0 &&
    option.score <= 2;

  validDecider &&
    dispatch(actions.setQuestionScore(question.virtual_id, option.score));
};

export const setConsent = (question, selectedOption) => dispatch => {
  const score = selectedOption?.score === 0 ? 2 : 0;
  const option = question.options.find(o => o.score === score);
  dispatch(updateOption("set", question, option));
};

export const preSelectOptions = questionnaire => dispatch => {
  questionnaire.questionnaire_sections.forEach(section => {
    section.questions.forEach(question => {
      question.options.forEach(option => {
        if (option.pre_selected) {
          dispatch(
            actions.addOption(
              question.virtual_id,
              option.virtual_id,
              option.id,
              {
                ts: new Date(),
                duration: 500,
                back_counter: 0,
              },
            ),
          );
        }
      });
    });
  });
};

export const clearSection = section => dispatch => {
  section.questions.forEach(question => {
    if (question.app_screen_type !== ScreenType.CONSENT) {
      question.app_screen_type === ScreenType.DRILLED_DOWN
        ? dispatch(clearSubOptions(question))
        : dispatch(actions.clearQuestion(question.virtual_id));
    }
  });
};

const clearSubOptions = question => (dispatch, getState) => {
  const { response } = getState();
  const questionResponse = response.questions[question.virtual_id] || {};
  keys(questionResponse.options).forEach(optionId =>
    dispatch(actions.clearSubOptions(question.virtual_id, optionId)),
  );
};

export const selectLanguage = (question, option) => dispatch => {
  dispatch(selectOption(question, option));
  const languageKey = option.text === "اردو" ? "text_urdu" : "text";
  const headingKey = option.text === "اردو" ? "heading_urdu" : "heading";
  dispatch(
    actions.setLanguageKey({
      langKey: languageKey,
      headingKey: headingKey,
    }),
  );
};

// QUESTION VALIDATION
// ==========================

const defaultValidator = (question, questionResponse, force = false) => {
  if (!force && !question.is_required) {
    return true;
  }
  return (
    size(questionResponse.options) > 0 ||
    question.kind === QuestionKind.DESCRIPTION
  );
};

const drilledDownOptionsValidator = (question, questionResponse) => {
  const { options: optionResponses = {} } = questionResponse;
  const minSelectionsRequired = question.min_option_selection_count;
  const maxSelectionsRequired = question.max_option_selection_count;
  const selectedOptions = size(optionResponses);
  const totalOptions = question.options.length;
  let minPossibleSelections = totalOptions;
  let maxPossibleSelections = totalOptions;

  if (!minSelectionsRequired && !maxSelectionsRequired) {
    return true;
  } else if (minSelectionsRequired && !maxSelectionsRequired) {
    minPossibleSelections = Math.min(
      minPossibleSelections,
      minSelectionsRequired,
    );
    return selectedOptions >= minPossibleSelections;
  } else if (maxSelectionsRequired && !minSelectionsRequired) {
    maxPossibleSelections = Math.min(
      minPossibleSelections,
      maxSelectionsRequired,
    );
    return selectedOptions <= maxPossibleSelections;
  } else {
    minPossibleSelections = Math.min(
      minPossibleSelections,
      minSelectionsRequired,
    );
    maxPossibleSelections = Math.min(
      maxPossibleSelections,
      maxSelectionsRequired,
    );
    return (
      selectedOptions >= minPossibleSelections &&
      selectedOptions <= maxPossibleSelections
    );
  }
};

const drilledDownValidator = (question, questionResponse) => {
  const { options: optionResponses = {} } = questionResponse;
  const minSelectionsRequired = question.min_sub_option_selection_count;
  const maxSelectionsRequired = question.max_sub_option_selection_count;

  if (!minSelectionsRequired && !maxSelectionsRequired) {
    return true;
  } else if (minSelectionsRequired && !maxSelectionsRequired) {
    return question.options.every(option => {
      const optionResponse = optionResponses[option.virtual_id] || {};
      const selectedSubOptions = size(optionResponse.subOptions);
      const totalSubOptions = option.sub_options.length;

      const minPossibleSelections = Math.min(
        totalSubOptions,
        minSelectionsRequired,
      );
      return selectedSubOptions >= minPossibleSelections;
    });
  } else if (maxSelectionsRequired && !minSelectionsRequired) {
    return question.options.every(option => {
      const optionResponse = optionResponses[option.virtual_id] || {};
      const selectedSubOptions = size(optionResponse.subOptions);
      const totalSubOptions = option.sub_options.length;

      const maxPossibleSelections = Math.min(
        totalSubOptions,
        maxSelectionsRequired,
      );
      return selectedSubOptions <= maxPossibleSelections;
    });
  } else {
    return question.options.every(option => {
      const optionResponse = optionResponses[option.virtual_id] || {};
      const selectedSubOptions = size(optionResponse.subOptions);
      const totalSubOptions = option.sub_options.length;
      const minPossibleSelections = Math.min(
        totalSubOptions,
        minSelectionsRequired,
      );
      const maxPossibleSelections = Math.min(
        totalSubOptions,
        maxSelectionsRequired,
      );
      return (
        selectedSubOptions >= minPossibleSelections &&
        selectedSubOptions <= maxPossibleSelections
      );
    });
  }
};

const textInputValidator = (question, questionResponse, state) => {
  if (!question.is_required) {
    return true;
  }
  const { options: optionResponses = {} } = questionResponse;
  const { errors: optionErrors } = state.response;
  const [optionId] = keys(optionResponses);

  if (!optionId || optionErrors[optionId]) {
    return false;
  }
  return optionResponses[optionId].other_text.length > 0;
};

const divisionValidator = (_, __, state) => {
  return !state.survey.divisionInfo.required || state.division.id;
};

const multipleOptionValidator = (question, questionResponse, state) => {
  if (question.is_master_question && !question.is_required) {
    return true;
  }
  if (!question.is_master_question) {
    return defaultValidator(question, questionResponse);
  }

  const { response, navigation } = state;
  const section = last(navigation.sectionStack);
  return section.questions
    .filter(q => question.id === q.master_question)
    .every(q => {
      const questionResp = response.questions[q.virtual_id] || {};
      return defaultValidator(q, questionResp, true);
    });
};

const validators = {
  DD: drilledDownValidator,
  CB: drilledDownOptionsValidator,
  OE: textInputValidator,
  TXI: textInputValidator,
  TXA: textInputValidator,
  CN: textInputValidator,
  EA: textInputValidator,
  DIV: divisionValidator,
  MQ: multipleOptionValidator,
};

export const validateCurrentSection = () => (dispatch, getState) => {
  const state = getState();
  const { response, navigation } = state;
  const section = last(navigation.sectionStack);
  const isValid = section.questions.every(question => {
    const questionResponse = response.questions[question.virtual_id] || {};
    const validator = validators[question.app_screen_type];
    if (validator) {
      return validator(question, questionResponse, state);
    } else {
      return defaultValidator(question, questionResponse);
    }
  });

  dispatch(actions.validateSection(isValid));
};

// RESPONSE TIMER
// ==========================

let lastResponseTimer = null;

const onNoResponse = dispatch => {
  dispatch(submitFeedback(false));
};

export const resetLastResponseTimer = () => (dispatch, getState) => {
  const state = getState();
  if (state.survey.forGo) {
    dispatch(discardLastResponseTimer());
    lastResponseTimer = setTimeout(() => onNoResponse(dispatch), 15000);
  }
};

export const discardLastResponseTimer = () => (_, getState) => {
  const state = getState();
  if (state.survey.forGo) {
    clearTimeout(lastResponseTimer);
  }
};

// OPTION ERRORS
// ==========================

export const setOptionError = actions.setOptionError;
export const clearOptionError = actions.clearOptionError;

// ITEMS
// ==========================

export const selectItem = (itemId, questionId, optionId, subOptionId) => (
  dispatch,
  getState,
) => {
  const { response } = getState();

  const option = response.questions[questionId].options[optionId];
  const subOption = option.subOptions?.[subOptionId];
  const o = subOption || option;
  const isSelected = o.products?.includes(itemId);

  const action = subOption
    ? isSelected
      ? actions.removeSubOptionItem
      : actions.addSubOptionItem
    : isSelected
    ? actions.removeOptionItem
    : actions.addOptionItem;

  dispatch(action(itemId, questionId, optionId, subOptionId));
};

export const selectItemForOption = itemId => (dispatch, getState) => {
  const { survey } = getState();
  const { questionId, optionId, subOptionId } = survey.linkedOptionForItem;
  dispatch(selectItem(itemId, questionId, optionId, subOptionId));
};

export const selectItemForQuestion = (question, item) => (
  dispatch,
  getState,
) => {
  const { response } = getState();
  const [option] = question.options;
  const isSelected =
    response.questions[question.virtual_id]?.options[option.virtual_id];
  if (!isSelected) {
    dispatch(selectOption(question, option));
  }

  dispatch(selectItem(item.id, question.virtual_id, option.virtual_id));
};
